import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import TickerBar from "./TickerBar";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, LineChart, Bell, FileText, Users2, Mail, Calculator,
  CreditCard, Sun, Moon, LogOut, Wheat, Menu, X, MessageCircle
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useEffect, useRef, useState } from "react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/markets", label: "Markets", icon: LineChart, testid: "nav-markets" },
  { to: "/app/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
  { to: "/app/emails", label: "Emails", icon: Mail, testid: "nav-emails" },
  { to: "/app/notes", label: "Analyst Notes", icon: FileText, testid: "nav-notes" },
  { to: "/app/vendors", label: "Vendors", icon: Users2, testid: "nav-vendors" },
  { to: "/app/simulator", label: "Cost Simulator", icon: Calculator, testid: "nav-simulator" },
  { to: "/app/analyst-chat", label: "Analyst Chat", icon: MessageCircle, testid: "nav-analyst-chat", enterpriseOnly: true },
  { to: "/app/pricing", label: "Plans", icon: CreditCard, testid: "nav-pricing" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const [openMobile, setOpenMobile] = useState(false);
  const [notes, setNotes] = useState({ items: [], unread: 0 });
  const seenIds = useRef(new Set());

  const loadNotes = async () => {
    try {
      const { data } = await api.get("/notifications");
      // Toast newly-arrived items
      data.items.forEach(n => {
        if (!seenIds.current.has(n.id) && !n.read) {
          seenIds.current.add(n.id);
          if (seenIds.current.size > 1) {
            toast.success(`${n.commodity_code}: ${n.message}`, { duration: 6000 });
          } else {
            seenIds.current.add(n.id);
          }
        } else {
          seenIds.current.add(n.id);
        }
      });
      setNotes(data);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    loadNotes();
    const id = setInterval(loadNotes, 30000);
    return () => clearInterval(id);
  }, [user]); // eslint-disable-line

  const markRead = async () => {
    await api.post("/notifications/read");
    await loadNotes();
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("agri_theme", dark ? "dark" : "light");
  }, [dark]);

  const tierColor = { free: "bg-muted text-muted-foreground", pro: "signal-buy border", enterprise: "signal-hold border" }[user?.tier || "free"];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-md">
        <div className="flex h-14 items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" data-testid="mobile-nav-toggle" onClick={() => setOpenMobile(v => !v)}>
              {openMobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">
                <Wheat className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-sm font-bold">AgriTerminal <span className="text-primary">Pro</span></div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Buying Consultant</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <Badge data-testid="tier-badge" variant="outline" className={`uppercase tracking-wider text-[10px] ${tierColor}`}>
                {user.tier}
              </Badge>
            )}
            <button data-testid="theme-toggle" onClick={() => setDark(v => !v)} className="rounded-md border border-border/60 p-2 hover:bg-secondary">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {user && (
              <DropdownMenu onOpenChange={(o) => { if (o) markRead(); }}>
                <DropdownMenuTrigger asChild>
                  <button data-testid="notifications-bell" className="relative rounded-md border border-border/60 p-2 hover:bg-secondary">
                    <Bell className="h-4 w-4" />
                    {notes.unread > 0 && (
                      <span data-testid="notifications-badge" className="absolute -top-1 -right-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-[hsl(var(--signal-wait))] px-1 text-[9px] font-mono font-bold text-white">
                        {notes.unread > 9 ? "9+" : notes.unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notes.items.length === 0 && (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">No notifications yet.<br/>Set an alert to get pinged here.</div>
                  )}
                  {notes.items.slice(0, 8).map(n => (
                    <DropdownMenuItem key={n.id} data-testid={`notification-${n.id}`} onClick={() => navigate(`/commodity/${n.commodity_code}`)} className="flex-col items-start gap-1">
                      <div className="flex w-full items-center justify-between">
                        <Badge variant="outline" className="uppercase text-[10px]">{n.commodity_code}</Badge>
                        <span className="text-[10px] font-mono text-muted-foreground">{new Date(n.created_at).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}</span>
                      </div>
                      <div className="text-xs">{n.message}</div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {user && (
              <div className="hidden sm:flex items-center gap-2 border-l border-border/60 pl-3">
                <div className="text-right leading-tight">
                  <div className="text-xs font-semibold">{user.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{user.persona}</div>
                </div>
                <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <TickerBar />
      </header>

      <div className="mx-auto flex max-w-[1600px]">
        {/* Sidebar */}
        <aside className={`fixed inset-y-0 left-0 top-14 z-30 w-56 shrink-0 border-r border-border/60 bg-card/40 backdrop-blur-md transition-transform lg:sticky lg:top-[100px] lg:h-[calc(100vh-100px)] lg:translate-x-0 ${openMobile ? "translate-x-0" : "-translate-x-full"}`}>
          <nav className="flex flex-col gap-1 p-3">
            {NAV.filter(n => !n.enterpriseOnly || user?.tier === "enterprise").map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/app"}
                data-testid={n.testid}
                onClick={() => setOpenMobile(false)}
                className={({isActive}) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition ${isActive
                    ? "bg-primary/15 text-primary font-semibold"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`
                }
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mx-3 mt-6 rounded-md border border-border/60 bg-background/50 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Data disclosure</div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Prices are simulated demo feeds. AI outputs are market insights, not financial advice.
            </p>
          </div>
        </aside>

        <main className="min-h-[calc(100vh-96px)] flex-1 px-4 py-6 lg:px-8 lg:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}
